import React, { useState, useMemo } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

// Property type config: maps sidebar id to display label, icon, and possible API values
const PROPERTY_TYPE_CONFIG = [
  { id: 'all', label: 'All Properties', icon: 'Building', color: 'text-foreground', apiValues: null },
  { id: 'vacant-plot', label: 'Vacant Plot', icon: 'MapPin', color: 'text-muted-foreground', apiValues: ['vacant plot'] },
  { id: 'land', label: 'Land', icon: 'TreePine', color: 'text-success', apiValues: ['land'] },
  { id: 'residential', label: 'Residential', icon: 'Home', color: 'text-accent', apiValues: ['residential'] },
  { id: 'commercial', label: 'Commercial', icon: 'Building2', color: 'text-primary', apiValues: ['commercial'] },
  { id: 'industrial', label: 'Industrial', icon: 'Factory', color: 'text-secondary', apiValues: ['industrial'] },
  { id: 'commercial-space', label: 'Commercial Space', icon: 'Store', color: 'text-primary', apiValues: ['commercial space'] },
  { id: 'flat', label: 'Flat', icon: 'Building', color: 'text-accent', apiValues: ['flat'] },
  { id: 'villa', label: 'Villa', icon: 'Home', color: 'text-warning', apiValues: ['villa'] },
  { id: 'under-construction', label: 'Under Construction', icon: 'HardHat', color: 'text-warning', apiValues: ['under construction', 'under construction'] }
];

const SURVEY_STATUS_CONFIG = [
  { id: 'assigned', label: 'Assigned Surveys', icon: 'UserCheck', color: 'text-primary', apiValues: null },
  { id: 'unassigned', label: 'Unassigned Surveys', icon: 'UserX', color: 'text-warning', apiValues: null },
  { id: 'pending', label: 'Pending Survey', icon: 'Clock', color: 'text-warning', apiValues: ['pending'] },
  { id: 'in-progress', label: 'In Progress', icon: 'Activity', color: 'text-accent', apiValues: ['in_progress', 'in progress'] },
  { id: 'completed', label: 'Completed', icon: 'CheckCircle', color: 'text-success', apiValues: ['completed'] },
  { id: 'requires-update', label: 'Needs Update', icon: 'AlertCircle', color: 'text-error', apiValues: ['requires_update', 'requires-update', 'needs update', 'needs_update'] }
];

const PropertySidebar = ({ onCategorySelect, selectedCategory, properties = [], propertySurveyStatus = {}, className = "" }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const categories = useMemo(() => {
    const total = properties?.length ?? 0;
    return PROPERTY_TYPE_CONFIG.map((config) => {
      let count = 0;
      if (config.id === 'all') {
        count = total;
      } else if (config.apiValues) {
        // property_type comes from survey_details, stored in propertySurveyStatus
        count = properties.filter((p) => {
          const pt = (propertySurveyStatus[String(p?.id)]?.propertyType || '').toLowerCase().trim();
          return config.apiValues.some((v) => pt === v || pt.includes(v));
        }).length;
      }
      return { ...config, count };
    });
  }, [properties, propertySurveyStatus]);

  const statusFilters = useMemo(() => {
    const statusCounts = { assigned: 0, unassigned: 0, pending: 0, 'in-progress': 0, completed: 0, 'requires-update': 0 };
    (properties ?? []).forEach((p) => {
      const surveyInfo = propertySurveyStatus[String(p?.id)];
      const hasSurvey = surveyInfo && surveyInfo.surveyNumber;
      const status = (surveyInfo?.status || '').toString().toLowerCase().replace(/\s+/g, '_');
      if (hasSurvey) {
        statusCounts.assigned += 1;
      } else {
        statusCounts.unassigned += 1;
      }
      if (!hasSurvey) {
        statusCounts.pending += 1;
      } else if (['in_progress', 'in progress'].includes(status)) {
        statusCounts['in-progress'] += 1;
      } else if (status === 'completed') {
        statusCounts.completed += 1;
      } else if (['requires_update', 'requires-update', 'needs_update', 'needs update'].some((s) => status.includes(s))) {
        statusCounts['requires-update'] += 1;
      } else {
        statusCounts.pending += 1;
      }
    });
    return SURVEY_STATUS_CONFIG.map((config) => ({
      ...config,
      count: statusCounts[config.id] ?? 0
    }));
  }, [properties, propertySurveyStatus]);

  const quickActions = [
    {
      id: 'recent',
      label: 'Recently Added',
      icon: 'Plus',
      description: 'Properties added in last 7 days'
    },
    {
      id: 'high-value',
      label: 'High Value',
      icon: 'TrendingUp',
              description: 'Properties over ₹1M'
    },
    {
      id: 'expired',
      label: 'Expired Valuations',
      icon: 'AlertTriangle',
      description: 'Valuations older than 6 months'
    },
    {
      id: 'favorites',
      label: 'Favorites',
      icon: 'Star',
      description: 'Your starred properties'
    }
  ];

  return (
    <div className={`bg-card border-r border-border transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-64'} ${className}`}>
      {/* Sidebar Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        {!isCollapsed && (
          <h2 className="text-lg font-semibold text-foreground">Property Categories</h2>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="ml-auto"
        >
          <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={20} />
        </Button>
      </div>
      <div className="overflow-y-auto h-full pb-4">
        {/* Property Types */}
        <div className="p-4">
          {!isCollapsed && (
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Property Types
            </h3>
          )}
          <div className="space-y-1">
            {categories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => onCategorySelect(category?.id)}
                className={`
                  w-full flex items-center justify-between p-2 rounded-md text-left transition-colors
                  ${selectedCategory === category?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                  }
                `}
                title={isCollapsed ? category?.label : ''}
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={category?.icon} 
                    size={18} 
                    color={selectedCategory === category?.id ? 'currentColor' : category?.color}
                  />
                  {!isCollapsed && (
                    <span className="text-sm font-medium">{category?.label}</span>
                  )}
                </div>
                {!isCollapsed && (
                  <span className={`
                    text-xs px-2 py-1 rounded-full
                    ${selectedCategory === category?.id
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                    }
                  `}>
                    {category?.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Status Filters */}
        <div className="p-4 border-t border-border">
          {!isCollapsed && (
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Survey Status
            </h3>
          )}
          <div className="space-y-1">
            {statusFilters?.map((status) => (
              <button
                key={status?.id}
                onClick={() => onCategorySelect(status?.id)}
                className={`
                  w-full flex items-center justify-between p-2 rounded-md text-left transition-colors
                  ${selectedCategory === status?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                  }
                `}
                title={isCollapsed ? status?.label : ''}
              >
                <div className="flex items-center space-x-3">
                  <Icon 
                    name={status?.icon} 
                    size={18} 
                    color={selectedCategory === status?.id ? 'currentColor' : status?.color}
                  />
                  {!isCollapsed && (
                    <span className="text-sm font-medium">{status?.label}</span>
                  )}
                </div>
                {!isCollapsed && (
                  <span className={`
                    text-xs px-2 py-1 rounded-full
                    ${selectedCategory === status?.id
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                    }
                  `}>
                    {status?.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="p-4 border-t border-border">
          {!isCollapsed && (
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Quick Filters
            </h3>
          )}
          <div className="space-y-1">
            {quickActions?.map((action) => (
              <button
                key={action?.id}
                onClick={() => onCategorySelect(action?.id)}
                className={`
                  w-full flex items-center p-2 rounded-md text-left transition-colors
                  ${selectedCategory === action?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-foreground'
                  }
                `}
                title={isCollapsed ? action?.label : action?.description}
              >
                <Icon name={action?.icon} size={18} className="mr-3" />
                {!isCollapsed && (
                  <div>
                    <div className="text-sm font-medium">{action?.label}</div>
                    <div className="text-xs text-muted-foreground">{action?.description}</div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Sidebar Footer */}
        {!isCollapsed && (
          <div className="p-4 border-t border-border">
            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Info" size={16} color="var(--color-primary)" />
                <span className="text-sm font-medium text-foreground">Quick Tip</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Use bulk actions to efficiently manage multiple properties at once. 
                Select properties and choose from scheduling, reporting, or status updates.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertySidebar;